import { useCallback, useEffect, useMemo, useState } from "react";
import { ActionIcon, Button, Flex, NumberInput, TextInput } from "@mantine/core";
import styled from "styled-components";
import CustomSelect from "../common/Inputs/CustomSelect";
import CustomDatePicker from "../common/Inputs/CustomDatePicker";
import RotationMatrix from "./RotationMatrix";
import { getDepartmentSelectData } from "../../api/departmentApi";
import { getShiftDefinitionSelectData } from "../../api/shiftDefinitionApi";
import {
  RotationPeriod,
  RotationPeriodType,
  ShiftRotation,
  getShiftTeamSelectData,
  previewDraftRotation,
} from "../../api/shiftRotationApi";
import { toDayKey, todayKey } from "../../utils/shiftPattern";

/**
 * ROTA KURMA FORMU (plan 2.34).
 *
 * Rota iki SIRALI listeden ibaret: ekipler ve çevrimdeki vardiyalar. Sıra anlam
 * taşıyor — ekibin çevrimdeki yeri `teamIds` içindeki indeksi, ve
 * `(dönem + sıra) % vardiyaSayısı` o dönemin vardiyasını veriyor.
 *
 * ⚠ ÇEVRİMDE "DİNLENME" SLOTU OLABİLİR (21 Ağu kararı): 4 ekip / 3 vardiya
 * düzeni ancak böyle ifade edilebiliyor ve fabrikaların gerçekte kurduğu düzen
 * bu. Formda ayrı bir seçenek olarak duruyor.
 *
 * ⚠ CANLI ÖNİZLEME BACKEND'DEN: kullanıcı listeleri değiştirdikçe
 * `/preview-draft` çağrılıyor ve matris oradan geliyor. Panelde hesaplamak daha
 * hızlı olurdu ama İK'nın kaydetmeden gördüğü tablo, kaydettikten sonrakinden
 * farklı çıkabilirdi — üstelik yürürlüğe girmiş rota SİLİNEMİYOR, yani yanlış
 * kaydın bedeli yüksek.
 */

/** Çevrimde dinlenmeyi temsil eden sentinel — `null` Select'te taşınamıyor. */
const REST = "__rest__";

export type RotationFormValues = {
  name: string;
  departmentId: string | null;
  teamIds: (string | null)[];
  shiftDefinitionIds: string[]; // REST ya da tanım id'si
  periodType: RotationPeriodType;
  periodCount: number;
  anchorDate: string | null;
  startDate: string | null;
  endDate: string | null;
};

export const emptyRotationForm = (): RotationFormValues => ({
  name: "",
  departmentId: null,
  teamIds: [null, null],
  shiftDefinitionIds: [REST, REST],
  periodType: "week",
  periodCount: 1,
  anchorDate: todayKey(),
  startDate: todayKey(),
  endDate: null,
});

export const formFromRotation = (rotation: ShiftRotation): RotationFormValues => ({
  name: rotation.name,
  departmentId: rotation.departmentId,
  teamIds: rotation.teamIds,
  shiftDefinitionIds: rotation.shiftDefinitionIds.map((id) => id ?? REST),
  periodType: rotation.periodType,
  periodCount: rotation.periodCount,
  anchorDate: rotation.anchorDate,
  startDate: rotation.startDate,
  endDate: rotation.endDate ?? null,
});

/** Formu backend'in beklediği gövdeye çevirir (REST → `null`). */
export const payloadOf = (form: RotationFormValues) => ({
  name: form.name.trim(),
  departmentId: form.departmentId!,
  teamIds: form.teamIds.filter((id): id is string => Boolean(id)),
  shiftDefinitionIds: form.shiftDefinitionIds.map((id) =>
    id === REST ? null : id
  ),
  periodType: form.periodType,
  periodCount: form.periodCount,
  anchorDate: toDayKey(form.anchorDate)!,
  startDate: toDayKey(form.startDate)!,
  endDate: toDayKey(form.endDate),
});

const RotationForm = ({
  value,
  onChange,
  /** Düzenlemede departman KİLİTLİ — backend de değiştirmeye izin vermiyor. */
  departmentLocked = false,
}: {
  value: RotationFormValues;
  onChange: (next: RotationFormValues) => void;
  departmentLocked?: boolean;
}) => {
  const [departmentOptions, setDepartmentOptions] = useState<any[]>([]);
  const [teamOptions, setTeamOptions] = useState<any[]>([]);
  const [shiftOptions, setShiftOptions] = useState<any[]>([]);

  const [periods, setPeriods] = useState<RotationPeriod[]>([]);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const set = (patch: Partial<RotationFormValues>) =>
    onChange({ ...value, ...patch });

  useEffect(() => {
    getDepartmentSelectData()
      .then(({ data }: any) => setDepartmentOptions(data?.data ?? []))
      .catch(() => setDepartmentOptions([]));
  }, []);

  // Ekipler ve vardiyalar DEPARTMANA bağlı okunuyor: rotaya yalnızca o
  // departmanın ekibi girebilir (backend de reddediyor), vardiya listesi de
  // departmana ait olanlar + şirket geneli olanlarla sınırlanıyor.
  useEffect(() => {
    if (!value.departmentId) {
      setTeamOptions([]);
      setShiftOptions([]);
      return;
    }
    getShiftTeamSelectData(value.departmentId)
      .then((data: any) => setTeamOptions(data?.data ?? []))
      .catch(() => setTeamOptions([]));
    getShiftDefinitionSelectData(value.departmentId)
      .then((data: any) => setShiftOptions(data?.data ?? []))
      .catch(() => setShiftOptions([]));
  }, [value.departmentId]);

  const payload = useMemo(() => {
    if (!value.departmentId) return null;
    const p = payloadOf(value);
    if (p.teamIds.length === 0 || !p.anchorDate || !p.startDate) return null;
    if (p.shiftDefinitionIds.every((id) => id == null)) return null;
    return p;
  }, [value]);

  const loadPreview = useCallback(async () => {
    if (!payload) {
      setPeriods([]);
      setPreviewError(null);
      return;
    }
    const response: any = await previewDraftRotation({
      ...payload,
      from: payload.startDate,
      // Dört dönem, çevrimin tamamını görmeye yetmezse vardiya sayısı kadar:
      // çevrim ancak bir tam tur görülünce anlaşılıyor.
      periods: Math.max(4, payload.shiftDefinitionIds.length),
    });
    if (response?.success) {
      setPeriods(response.data.periods ?? []);
      setPreviewError(null);
    } else {
      setPeriods([]);
      setPreviewError(response?.message ?? "Önizleme alınamadı.");
    }
  }, [payload]);

  useEffect(() => {
    loadPreview();
  }, [loadPreview]);

  // ── Sıralı liste düzenleyicileri ────────────────────────────────────────
  const moveTeam = (index: number, delta: number) => {
    const next = [...value.teamIds];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    set({ teamIds: next });
  };

  const moveShift = (index: number, delta: number) => {
    const next = [...value.shiftDefinitionIds];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    set({ shiftDefinitionIds: next });
  };

  const shiftSelectData = useMemo(
    () => [{ value: REST, label: "— Dinlenme —" }, ...shiftOptions],
    [shiftOptions]
  );

  return (
    <Flex direction="column" gap={28}>
      <Flex gap={20} wrap="wrap" align="flex-end">
        <TextInput
          label="Rota Adı"
          placeholder="Üretim 3'lü Çevrim"
          withAsterisk
          w={280}
          value={value.name}
          onChange={(e) => set({ name: e.currentTarget.value })}
        />
        <CustomSelect
          label="Departman"
          placeholder="Departman Seçiniz"
          withAsterisk
          w={240}
          data={departmentOptions}
          value={value.departmentId}
          disabled={departmentLocked}
          description={
            departmentLocked
              ? "Departman değiştirilemez; yeni departman için yeni rota açın."
              : undefined
          }
          onChange={(id: string | null) =>
            // Departman değişince ekip seçimleri geçersiz kalır — temizleniyor.
            set({ departmentId: id, teamIds: value.teamIds.map(() => null) })
          }
          searchable
        />
      </Flex>

      <Columns>
        <Column>
          <ColumnTitle>Ekipler (çevrim sırası)</ColumnTitle>
          <ColumnHint>
            Sıra anlam taşıyor: listedeki 1. ekip çevrimin 1. vardiyasıyla
            başlar, 2. ekip 2. vardiyasıyla.
          </ColumnHint>
          {value.teamIds.map((teamId, index) => (
            <Row key={`team-${index}`}>
              <OrderTag>{index + 1}</OrderTag>
              <CustomSelect
                placeholder="Ekip Seçiniz"
                w="100%"
                data={teamOptions}
                value={teamId}
                onChange={(id: string | null) => {
                  const next = [...value.teamIds];
                  next[index] = id;
                  set({ teamIds: next });
                }}
                searchable
                disabled={!value.departmentId}
              />
              <ActionIcon
                variant="subtle"
                aria-label="Yukarı taşı"
                onClick={() => moveTeam(index, -1)}
                disabled={index === 0}
              >
                ↑
              </ActionIcon>
              <ActionIcon
                variant="subtle"
                aria-label="Aşağı taşı"
                onClick={() => moveTeam(index, 1)}
                disabled={index === value.teamIds.length - 1}
              >
                ↓
              </ActionIcon>
              <ActionIcon
                variant="subtle"
                color="red"
                aria-label="Kaldır"
                onClick={() =>
                  set({ teamIds: value.teamIds.filter((_, i) => i !== index) })
                }
                disabled={value.teamIds.length <= 1}
              >
                ×
              </ActionIcon>
            </Row>
          ))}
          <Button
            variant="light"
            color="indigo"
            onClick={() => set({ teamIds: [...value.teamIds, null] })}
          >
            Ekip Ekle
          </Button>
        </Column>

        <Column>
          <ColumnTitle>Çevrim (vardiya sırası)</ColumnTitle>
          <ColumnHint>
            Ekip sayısıyla eşit olmak zorunda değil.{" "}
            <strong>Dinlenme</strong> slotu koyarak 4 ekip / 3 vardiya düzeni
            kurulabilir.
          </ColumnHint>
          {value.shiftDefinitionIds.map((shiftId, index) => (
            <Row key={`shift-${index}`}>
              <OrderTag>{index + 1}</OrderTag>
              <CustomSelect
                placeholder="Vardiya Seçiniz"
                w="100%"
                data={shiftSelectData}
                value={shiftId}
                onChange={(id: string | null) => {
                  const next = [...value.shiftDefinitionIds];
                  next[index] = id ?? REST;
                  set({ shiftDefinitionIds: next });
                }}
                searchable
                disabled={!value.departmentId}
              />
              <ActionIcon
                variant="subtle"
                aria-label="Yukarı taşı"
                onClick={() => moveShift(index, -1)}
                disabled={index === 0}
              >
                ↑
              </ActionIcon>
              <ActionIcon
                variant="subtle"
                aria-label="Aşağı taşı"
                onClick={() => moveShift(index, 1)}
                disabled={index === value.shiftDefinitionIds.length - 1}
              >
                ↓
              </ActionIcon>
              <ActionIcon
                variant="subtle"
                color="red"
                aria-label="Kaldır"
                onClick={() =>
                  set({
                    shiftDefinitionIds: value.shiftDefinitionIds.filter(
                      (_, i) => i !== index
                    ),
                  })
                }
                disabled={value.shiftDefinitionIds.length <= 1}
              >
                ×
              </ActionIcon>
            </Row>
          ))}
          <Button
            variant="light"
            color="indigo"
            onClick={() =>
              set({ shiftDefinitionIds: [...value.shiftDefinitionIds, REST] })
            }
          >
            Vardiya Ekle
          </Button>
        </Column>
      </Columns>

      <Flex gap={20} wrap="wrap" align="flex-end">
        <CustomSelect
          label="Dönüş Periyodu"
          w={180}
          data={[
            { value: "week", label: "Haftalık" },
            { value: "month", label: "Aylık" },
          ]}
          value={value.periodType}
          onChange={(v: string | null) =>
            set({ periodType: (v as RotationPeriodType) ?? "week" })
          }
        />
        <NumberInput
          label="Kaç periyotta bir"
          description="2 = iki haftada bir döner"
          w={180}
          min={1}
          max={52}
          value={value.periodCount}
          onChange={(v) => set({ periodCount: Number(v) || 1 })}
        />
        <CustomDatePicker
          label="Çevrim Başlangıcı"
          withAsterisk
          value={value.anchorDate}
          onChange={(v: any) => set({ anchorDate: v })}
        />
        <CustomDatePicker
          label="Geçerlilik Başlangıcı"
          withAsterisk
          value={value.startDate}
          onChange={(v: any) => set({ startDate: v })}
        />
        <CustomDatePicker
          label="Geçerlilik Bitişi (Opsiyonel)"
          placeholder="Süresiz"
          value={value.endDate}
          onChange={(v: any) => set({ endDate: v })}
          minDate={value.startDate ?? undefined}
        />
      </Flex>

      <AnchorHint>
        <strong>Çevrim başlangıcı</strong> ile <strong>geçerlilik başlangıcı</strong>{" "}
        aynı şey değil: çevrim başlangıcı dönüşün FAZINI belirler (1. dönem ne
        zaman başladı), geçerlilik ise rotanın ne zamandan itibaren uygulandığını.
        Haftalık rotada dönüş sınırı, çevrim başlangıcının hafta gününde geçer —
        Pazartesi dönmesi isteniyorsa bir Pazartesi seçin.
      </AnchorHint>

      <div>
        <ColumnTitle>Önizleme</ColumnTitle>
        <ColumnHint>
          Kaydedilecek çevrimin ilk dönemleri. Bu tablo, kaydettikten sonra
          göreceğinizin aynısıdır.
        </ColumnHint>
        {previewError ? (
          <PreviewError>{previewError}</PreviewError>
        ) : (
          <RotationMatrix periods={periods} periodType={value.periodType} />
        )}
      </div>
    </Flex>
  );
};

export default RotationForm;

const Columns = styled.div`
  display: flex;
  gap: 40px;
  flex-wrap: wrap;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 340px;
  flex: 1;
`;

const ColumnTitle = styled.div`
  color: #111827;
  font-size: 16px;
  font-weight: 600;
`;

const ColumnHint = styled.div`
  color: #6b7280;
  font-size: 13px;
  line-height: 19px;
  margin-bottom: 4px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const OrderTag = styled.div`
  width: 24px;
  min-width: 24px;
  text-align: center;
  color: #5c26ff;
  font-size: 14px;
  font-weight: 600;
`;

const AnchorHint = styled.div`
  border-left: 3px solid #ddd6fe;
  padding-left: 14px;
  color: #6b7280;
  font-size: 13px;
  line-height: 20px;
  max-width: 780px;
`;

const PreviewError = styled.div`
  border: 1px dashed #fca5a5;
  border-radius: 6px;
  padding: 20px;
  color: #b91c1c;
  font-size: 14px;
`;
